/**
 * QueuedSortableRow — one draggable queued element. The drag order IS the
 * priority: the top row of a layer is the animated "NEXT UP", the rest show a
 * muted rank. Only the grip starts a drag; the timeline button stays clickable.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Loader2 } from 'lucide-react';

import {
  IconMoveToLive,
  IconMoveToWaiting,
  queueActionGlyph,
  queueActionSurface,
} from '../../queue-action-style';
import type { QueueElement } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { useMarkReady } from '../../../../hooks/useMarkReady';
import { layerIcon } from '../../queue-icons';
import SelectCheckbox from '../../bulk/row/SelectCheckbox';
import type { BulkAwareProps } from '../../bulk/row/bulk-row.types';
import MoveToLiveButton from '../../promote/MoveToLiveButton';
import { layerLabel } from '../../queue-status.util';
import { RuleBadge } from '../queue-cells';
import QueueElementCapChips from '../../../../components/cap-usage/QueueElementCapChips';
import QueueTimelineCell from '../../timeline/QueueTimelineCell';

export interface QueuedSortableRowProps extends BulkAwareProps {
  element: QueueElement;
  /** 1-based position in its layer (top = 1 = highest priority). */
  rank: number;
  isNext: boolean;
  /** Name of the live element this queued one replaces on the next run. */
  replaces?: string;
  campaignId: string;
  provider: QueueProvider;
  /** Lock the grip while the reorder save is in flight. */
  pending: boolean;
}

const QueuedSortableRow: FC<QueuedSortableRowProps> = ({
  element,
  rank,
  isNext,
  replaces,
  campaignId,
  provider,
  pending,
  bulk,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: element.bankId });
  const markReady = useMarkReady(campaignId, provider);
  const Icon = layerIcon(element.layer);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-md border px-2 py-1.5',
        isNext ? 'border-primary/40 bg-primary/5' : 'border-border/60 bg-card/40',
        isDragging && 'z-10 opacity-60 shadow-lg',
      )}
    >
      {bulk ? (
        <SelectCheckbox
          element={element}
          checked={bulk.isSelected(element)}
          onToggle={bulk.onToggleSelect}
          disabled={!bulk.selectable}
          status={bulk.statusOf?.(element)}
        />
      ) : null}
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder ${element.itemName}`}
        className={cn(
          'flex h-6 w-5 shrink-0 touch-none cursor-grab items-center justify-center rounded text-muted-foreground/60 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
          pending && 'pointer-events-none opacity-40',
        )}
      >
        <GripVertical className="h-4 w-4" aria-hidden />
      </button>
      {isNext ? (
        <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary motion-safe:animate-next-up">
          <IconMoveToLive className="h-3 w-3" aria-hidden />
          Next up
        </span>
      ) : (
        <span className="w-5 shrink-0 text-center text-[11px] font-semibold tabular-nums text-muted-foreground">
          {rank}
        </span>
      )}
      <Icon
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
        aria-label={layerLabel(element.layer)}
      />
      <span className="min-w-0 truncate text-sm font-medium" title={element.itemName}>
        {element.itemName}
      </span>
      <span className="shrink-0">
        <RuleBadge rule={element.rule} />
      </span>
      <QueueElementCapChips element={element} provider={provider} />
      {replaces ? (
        <span className="shrink-0 text-[10px] text-muted-foreground">
          replaces <span className="font-medium text-foreground">{replaces}</span>
        </span>
      ) : null}
      {element.timesQueued > 1 ? (
        <span
          className="shrink-0 text-[10px] tabular-nums text-muted-foreground"
          title={`Entered the queue ${element.timesQueued} times`}
        >
          ×{element.timesQueued}
        </span>
      ) : null}
      <span className="ml-auto shrink-0">
        <QueueTimelineCell element={element} campaignId={campaignId} provider={provider} />
      </span>
      {/* Labelled and toned, exactly like the Live / Waiting / Eliminated rows:
       *  a bare icon made this the one tab where you had to hover to learn what
       *  the action was. */}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={cn('h-7 shrink-0 gap-1 text-[11px]', queueActionSurface('waiting'))}
        title="Move to waiting — leaves the promotion line until moved back to the queue"
        disabled={markReady.isPending}
        onClick={() =>
          markReady.mutate({
            layer: element.layer,
            bankId: element.bankId,
            ready: false,
          })
        }
      >
        {/* The write settles in milliseconds but the row only moves once the
         *  queue re-reads the tracker — seconds. Without the spinner the button
         *  just dimmed and sat there, which reads as a dead button. */}
        {markReady.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          <IconMoveToWaiting
            className={cn('h-3.5 w-3.5', queueActionGlyph('waiting'))}
            aria-hidden
          />
        )}
        Move to waiting
      </Button>
      <MoveToLiveButton element={element} busy={markReady.isPending} />
    </div>
  );
};

export default QueuedSortableRow;
